// src/components/ui/Field.jsx
import React, { useState } from "react";

/**
 * Form field — light theme. Light input background, navy text,
 * green focus ring.
 */
export default function Field({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  hint,
  rightSlot,
  onPanel = false,
  className = "",
  ...rest
}) {
  // When the field sits on a dark navy panel (contact form), invert the
  // label color so it stays legible.
  const labelColor = onPanel ? "var(--on-panel)" : "var(--txt)";

  return (
    <div className={`mb-3.5 ${className}`}>
      {label && (
        <label
          className="block text-[12px] font-medium mb-1.5 tracking-wide"
          style={{ color: labelColor }}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={[
            "w-full px-3.5 py-[11px] text-[14px] outline-none transition-colors",
            "rounded-full border",
            "placeholder:text-[var(--txt-3)]",
            error
              ? "border-[var(--red-border)] focus:border-[var(--red)]"
              : "border-[var(--border-2)] focus:border-[var(--gold)]",
            rightSlot ? "pr-11" : "",
          ].join(" ")}
          style={{
            background: onPanel ? "#FFFFFF" : "var(--surf-2)",
            color: "var(--txt)",
          }}
          {...rest}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            {rightSlot}
          </div>
        )}
      </div>
      {error && (
        <p
          className="mt-1.5 text-[11px] flex items-center gap-1"
          style={{ color: "var(--red)" }}
        >
          <i className="ti ti-alert-circle" style={{ fontSize: 12 }} /> {error}
        </p>
      )}
      {!error && hint && (
        <p className="mt-1.5 text-[11px]" style={{ color: "var(--txt-3)" }}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function PasswordField({
  label = "Password",
  value,
  onChange,
  placeholder = "Min. 8 characters",
  showStrength = false,
  error,
  hint,
  onPanel = false,
  ...rest
}) {
  const [visible, setVisible] = useState(false);

  const strength = (() => {
    const v = String(value || "");
    if (!v) return 0;
    if (v.length < 4) return 1;
    if (v.length < 6) return 2;
    if (v.length < 8) return 3;
    return 4;
  })();

  const segColor = [
    "",
    "var(--red)",
    "#F0A957",
    "var(--gold-lt)",
    "var(--teal)",
  ][strength];

  return (
    <div>
      <Field
        label={label}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        error={error}
        hint={hint}
        onPanel={onPanel}
        rightSlot={
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="text-[var(--txt-2)] hover:text-[var(--txt)] transition-colors"
            tabIndex={-1}
            aria-label={visible ? "Hide password" : "Show password"}
          >
            <i
              className={`ti ti-${visible ? "eye-off" : "eye"}`}
              style={{ fontSize: 16 }}
            />
          </button>
        }
        {...rest}
      />
      {showStrength && (
        <div className="flex gap-1 -mt-2 mb-3.5">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[3px] flex-1 rounded-full transition-colors duration-300"
              style={{
                background: i <= strength ? segColor : "var(--surf-3)",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
